import React, { useEffect, useRef, useState } from 'react';
import { X, Search, ChevronLeft, Dumbbell, Plus, Trash2 } from 'lucide-react';
import Button from '../../../../common/Button/Button';
import YouTubeEmbed from '../../../../common/YouTubeEmbed/YouTubeEmbed';
import OuterModalListItem from '../../../../common/OuterModalListItem/OuterModalListItem';
import { resolveExerciseCatalogId } from '../../core/api/workoutApi';

export const DEFAULT_EXERCISE_SET = {
  reps: 15,
  time_seconds: 50,
  rest_time_seconds: 30,
  target_weight: 0,
};

export const DEFAULT_WORKOUT_REST = 60;

const SEARCH_MIN = 2;

function ExerciseFormModal({
  isOpen,
  onClose,
  isEditMode,
  initialWorkout,
  kgWeightUnitId,
  onSearch,
  onSave,
  saving,
}) {
  const searchInputRef = useRef(null);
  const onSearchRef = useRef(onSearch);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [workoutRest, setWorkoutRest] = useState(DEFAULT_WORKOUT_REST);
  const [sets, setSets] = useState([{ ...DEFAULT_EXERCISE_SET }]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode && initialWorkout) {
      setSelected({
        id: initialWorkout.id,
        exercise_id: initialWorkout.exercise_id,
        workout_type: initialWorkout.workout_type || 'Exercise',
        short_demo: initialWorkout.short_demo || '',
        base_calories_per_rep: initialWorkout.base_calories_per_rep,
        sequence_number: initialWorkout.sequence_order || 1,
      });
      setWorkoutRest(initialWorkout.rest_time_after ?? DEFAULT_WORKOUT_REST);
      setSets(
        (initialWorkout.sets || []).map((set) => ({
          id: set.set_id || set.id,
          reps: set.reps ?? set.target_reps ?? DEFAULT_EXERCISE_SET.reps,
          time_seconds: set.time_seconds ?? set.target_time_seconds ?? DEFAULT_EXERCISE_SET.time_seconds,
          rest_time_seconds: set.rest_time_seconds ?? set.target_rest_seconds ?? DEFAULT_EXERCISE_SET.rest_time_seconds,
          target_weight: set.target_weight ?? set.weight ?? DEFAULT_EXERCISE_SET.target_weight,
          is_deleted: set.is_deleted || false,
          weight_unit_id: set.weight_unit_id || set.target_weight_unit_id || kgWeightUnitId,
          target_weight_unit_id: set.target_weight_unit_id || set.weight_unit_id || kgWeightUnitId,
        }))
      );
      setSearchInput('');
      setSearchResults([]);
    } else {
      setSelected(null);
      setWorkoutRest(DEFAULT_WORKOUT_REST);
      setSets([{ ...DEFAULT_EXERCISE_SET, weight_unit_id: kgWeightUnitId, target_weight_unit_id: kgWeightUnitId }]);
      setSearchInput('');
      setSearchResults([]);
    }
    // Intentionally omit kgWeightUnitId — utils often load after open and must not wipe edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, initialWorkout]);

  // Backfill kg unit ids once common utils arrive, without resetting other fields.
  useEffect(() => {
    if (!isOpen || !kgWeightUnitId) return;
    setSets((prev) =>
      prev.map((set) => ({
        ...set,
        weight_unit_id: set.weight_unit_id || kgWeightUnitId,
        target_weight_unit_id: set.target_weight_unit_id || kgWeightUnitId,
      }))
    );
  }, [isOpen, kgWeightUnitId]);

  useEffect(() => {
    if (isOpen && !selected && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 80);
    }
  }, [isOpen, selected]);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (!isOpen || selected || isEditMode) return;
    const q = searchInput.trim();
    if (q.length < SEARCH_MIN) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await onSearchRef.current(q);
        setSearchResults(results || []);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, isOpen, selected, isEditMode]);

  useEffect(() => {
    const handleKey = (e) => {
      if (isOpen && e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const visibleSets = sets.filter((s) => !s.is_deleted);

  const handleSelectExercise = (item) => {
    const catalogId = resolveExerciseCatalogId(item);
    if (!catalogId) return;
    setSelected({
      id: null,
      exercise_id: catalogId,
      workout_id: catalogId,
      workout_type: item.exercise || item.name || 'Exercise',
      short_demo: item.short_demo || '',
      base_calories_per_rep: item.base_calories_per_rep,
    });
    setSets([{
      ...DEFAULT_EXERCISE_SET,
      weight_unit_id: kgWeightUnitId,
      target_weight_unit_id: kgWeightUnitId,
    }]);
    setWorkoutRest(DEFAULT_WORKOUT_REST);
  };

  const updateSet = (index, field, value) => {
    setSets((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value === '' ? '' : Number(value) };
      return next;
    });
  };

  const addSet = () =>
    setSets((prev) => [
      ...prev,
      { ...DEFAULT_EXERCISE_SET, weight_unit_id: kgWeightUnitId, target_weight_unit_id: kgWeightUnitId },
    ]);

  const deleteSet = (index) =>
    setSets((prev) => {
      const next = [...prev];
      if (next[index].id) next[index] = { ...next[index], is_deleted: true };
      else next.splice(index, 1);
      return next;
    });

  const handleSubmit = () => {
    if (!selected) return;
    const validSets = visibleSets.filter(
      (s) => Number(s.reps) > 0 || Number(s.time_seconds) > 0
    );
    if (validSets.length === 0) return;
    onSave(
      selected,
      {
        workout_rest_time: workoutRest,
        ...(isEditMode && selected.sequence_number
          ? { sequence_number: selected.sequence_number }
          : {}),
      },
      sets
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-5 py-4 border-b border-gray-100 bg-gray-50/80">
          {selected && !isEditMode ? (
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Change exercise
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-orange-100">
                <Dumbbell className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEditMode ? 'Edit exercise' : 'Add exercise'}
              </h2>
            </div>
          )}
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!selected && !isEditMode && (
          <div className="shrink-0 px-5 py-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-orange-primary focus:outline-none"
                placeholder="Search exercises (e.g. squat, push up)"
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto min-h-0 p-5">
          {!selected && !isEditMode ? (
            searchLoading ? (
              <p className="text-center text-gray-500 py-8">Searching…</p>
            ) : searchResults.length > 0 ? (
              <div className="grid grid-cols-1 gap-2">
                {searchResults.map((item, idx) => (
                  <OuterModalListItem
                    key={`${resolveExerciseCatalogId(item) || idx}-${idx}`}
                    name={item.exercise || item.name || item.exercise_name || 'Unnamed exercise'}
                    subtitle={item.muscle_group || item.body_part || undefined}
                    clickHandler={() => handleSelectExercise(item)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">
                {searchInput.trim().length >= SEARCH_MIN ? 'No exercises found' : 'Type to search exercises'}
              </p>
            )
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 p-4">
                <p className="text-sm font-medium text-orange-800/80">Exercise</p>
                <h3 className="text-base font-semibold text-gray-900 mt-0.5">{selected?.workout_type}</h3>
              </div>

              {selected?.short_demo && (
                <YouTubeEmbed url={selected.short_demo} />
              )}

              {visibleSets.map((set, displayIndex) => {
                let actualIndex = -1;
                let count = 0;
                for (let i = 0; i < sets.length; i++) {
                  if (!sets[i].is_deleted) {
                    if (count === displayIndex) { actualIndex = i; break; }
                    count++;
                  }
                }
                return (
                  <div key={set.id || displayIndex} className="relative bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="absolute -top-3 left-3 bg-orange-primary text-white px-2 py-0.5 rounded text-xs">
                      Set {displayIndex + 1}
                    </div>
                    {visibleSets.length > 1 && (
                      <button type="button" onClick={() => deleteSet(actualIndex)} className="absolute top-2 right-2 text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                      {[
                        { label: 'Reps', field: 'reps' },
                        { label: 'Time (s)', field: 'time_seconds' },
                        { label: 'Rest (s)', field: 'rest_time_seconds' },
                        { label: 'Weight (kg)', field: 'target_weight' },
                      ].map(({ label, field }) => (
                        <div key={field}>
                          <label className="text-xs font-medium text-gray-600">{label}</label>
                          <input
                            type="number"
                            step="any"
                            value={set[field] ?? ''}
                            onChange={(e) => updateSet(actualIndex, field, e.target.value)}
                            className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:border-orange-primary focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={addSet}
                className="flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700"
              >
                <Plus className="w-4 h-4" /> Add set
              </button>

              <div>
                <label className="text-sm font-medium text-gray-700">Rest after exercise (sec)</label>
                <input
                  type="number"
                  value={workoutRest}
                  onChange={(e) => setWorkoutRest(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-orange-primary focus:outline-none"
                />
              </div>

              <Button
                text={saving ? 'Saving…' : isEditMode ? 'Update' : 'Add exercise'}
                onClick={handleSubmit}
                disabled={
                  saving ||
                  visibleSets.every(
                    (s) => !(Number(s.reps) > 0 || Number(s.time_seconds) > 0)
                  )
                }
                className="w-full bg-orange-primary text-white py-2.5 rounded-lg disabled:opacity-60"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExerciseFormModal;
